/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Grid2 as Grid, Typography } from '@mui/material';

import Alert from '../Alert';

const PageContent = React.memo(
  ({
    id,
    headingId,
    heading,
    instructionalText,
    customStyles = {},
    errorMessage,
    children,
  }) => {
    const theme = useTheme();

    return (
      <Grid
        id={id}
        data-testid={id}
        container
        direction='column'
        spacing={4}
        sx={{
          boxShadow: 3,
          bgcolor: 'white',
          borderRadius: '6px',
          justifyContent: 'center',
          p: 8,
          m: { xs: theme.spacing(0, 4), md: '0' },
          ...customStyles,
        }}
      >
        <Grid sx={{ width: '100%', wordBreak: 'break-word' }}>
          <Typography
            id={headingId}
            data-testid={headingId}
            component='h1'
            variant='h3'
            textAlign='center'
          >
            {heading}
          </Typography>
        </Grid>
        {errorMessage && (
          <Alert
            id='alert'
            data-testid='alert'
            variant='error'
            message={errorMessage}
          />
        )}
        {instructionalText && (
          <Grid sx={{ width: '100%', wordBreak: 'break-word' }}>
            <Typography
              id='instructionalText'
              data-testid='instructionalText'
              component='p'
            >
              {instructionalText}
            </Typography>
          </Grid>
        )}
        <Grid>{children}</Grid>
      </Grid>
    );
  },
);

PageContent.propTypes = {
  id: PropTypes.string.isRequired,
  headingId: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  instructionalText: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  customStyles: PropTypes.object,
  children: PropTypes.node,
};

export default PageContent;
